import { useQuery } from "@tanstack/react-query";
import type { UsageResponse } from "@workcost/shared";
import { apiJson } from "./api";
import { useAuth } from "./AuthContext";
import { isMockApi } from "./estimateApi";

/**
 * ข้อมูลการใช้งานของเดือนนี้ จาก GET /usage
 * - estimatesUsed / estimatesLimit: สิทธิ์ประเมินที่ใช้ไป (รวมที่จองอยู่) และทั้งหมด
 * - aiCreditPercentLeft: AI credit ที่เหลือเป็น % (0-100) ห้ามแสดงเป็นหน่วยอื่นกับผู้ใช้
 * - resetsAt: วันที่รอบใหม่ (YYYY-MM-DD) Free = วันที่ 1 ของเดือนถัดไป, แพ็กเกจเสียเงิน = 30 วันจากวันเริ่มรอบ
 */
export interface UsageData {
  estimatesUsed: number;
  estimatesLimit: number;
  aiCreditPercentLeft: number;
  resetsAt: string;
  savedJobsCount: number;
  /** null = ไม่จำกัด */
  savedJobsLimit: number | null;
  quotationEnabled: boolean;
}

const BKK_MS = 7 * 3_600_000;

/**
 * วันที่รอบใหม่ (YYYY-MM-DD ตามเวลาไทย)
 * period.start จาก server เป็นวันที่ (Free: 2026-10-01) หรือเวลาเริ่มรอบแบบเต็ม (แพ็กเกจเสียเงิน: 2026-11-04T05:57:15.000Z)
 * อ่านไม่ได้ให้คืนส่วนวันที่ของค่าเดิม แทนที่จะทำให้หน้าบัญชีทั้งส่วนโหลดไม่ได้
 */
export function resetDate(plan: UsageResponse["plan"], periodStart: string): string {
  const d = new Date(periodStart.length > 10 ? periodStart : `${periodStart}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return periodStart.slice(0, 10);
  if (plan === "free") return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString().slice(0, 10);
  return new Date(d.getTime() + 30 * 86_400_000 + BKK_MS).toISOString().slice(0, 10);
}

export function toUsageData(u: UsageResponse): UsageData {
  return { estimatesUsed: u.estimates.used, estimatesLimit: u.estimates.limit, aiCreditPercentLeft: u.aiCreditPercentLeft, resetsAt: resetDate(u.plan, u.period.start), savedJobsCount: u.savedJobs.count, savedJobsLimit: u.savedJobs.limit, quotationEnabled: u.quotationEnabled };
}

export const usageKey = ["usage"] as const;

async function loadUsage(): Promise<UsageData> {
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1") return toUsageData(await (await import("../mock/billing")).usage());
  return toUsageData(await apiJson<UsageResponse>("/usage"));
}

export function useUsage(): { data: UsageData | undefined; isError: boolean; isPending: boolean; refetch: () => void } {
  const { status } = useAuth();
  const q = useQuery({
    queryKey: usageKey,
    queryFn: loadUsage,
    enabled: status === "authenticated" || isMockApi(),
    staleTime: 30 * 1000,
  });
  return { data: q.data, isError: q.isError, isPending: q.isPending, refetch: () => void q.refetch() };
}

/** สิทธิ์ออกใบเสนอราคาตามค่าที่ตั้งในแพ็กเกจ (server เป็นผู้ตัดสิน) ระหว่างโหลดใช้แพ็กเกจ standard/premium เป็นค่าชั่วคราว */
export function useCanQuote(plan: string | undefined): boolean {
  const { data } = useUsage();
  return data?.quotationEnabled ?? (plan === "standard" || plan === "premium");
}
